import type { Theme } from '../../shared/themes';
import type { Entity, PropInstance } from '../../shared/track';
import { entityX, geyserActive } from '../../shared/track';
import type { ProjectedSegment } from './road';
import { hsl, noise, rgba, shade } from './palette';

/** World-unit heights, kept next to the drawing so art and physics agree. */
const H_ROCK = 340;
const H_LOG = 150;
const H_TRAFFIC = 330;
const H_RAMP = 190;
const H_ORB = 150;
const H_GEYSER = 900;
const GATE_TOP = 520;

/**
 * One colour per rule, used on every world. Gold means the answer is height,
 * red means the answer is a different lane. A player should never have to work
 * out which kind of obstacle they are looking at.
 */
const HOP_COLOUR = '#ffd24d';
const DODGE_COLOUR = '#ff4d5e';

type Hint = 'hop' | 'dodge' | 'duck';

/**
 * Floating marker above a hazard. It keeps a readable minimum size no matter
 * how far away the hazard is, which is what lets you plan a line early instead
 * of reacting once it is on top of you.
 */
function drawHint(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  kind: Hint,
  halfW: number,
  timeS: number,
): void {
  const size = Math.max(9, Math.min(30, halfW * 0.9));
  const alpha = Math.max(0, Math.min(1, (halfW - 2.5) / 12));
  if (alpha <= 0.02) return;
  const bob = Math.sin(timeS * 5) * size * 0.11;

  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.lineWidth = Math.max(1.6, size * 0.2);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (kind === 'dodge') {
    ctx.strokeStyle = DODGE_COLOUR;
    // Arrows point away from the hazard: go round, either side.
    for (const dir of [-1, 1]) {
      const ox = x + dir * size * 1.05;
      ctx.beginPath();
      ctx.moveTo(ox, y - size * 0.42);
      ctx.lineTo(ox + dir * size * 0.5, y);
      ctx.lineTo(ox, y + size * 0.42);
      ctx.stroke();
    }
  } else {
    const up = kind === 'hop';
    ctx.strokeStyle = up ? HOP_COLOUR : '#7cf7ff';
    const dir = up ? -1 : 1;
    for (let i = 0; i < 2; i++) {
      const oy = y + bob + dir * i * size * 0.46;
      ctx.beginPath();
      ctx.moveTo(x - size * 0.55, oy);
      ctx.lineTo(x, oy + dir * size * 0.5);
      ctx.lineTo(x + size * 0.55, oy);
      ctx.stroke();
    }
  }
  ctx.restore();
}

export function drawEntity(
  ctx: CanvasRenderingContext2D,
  e: Entity,
  rec: ProjectedSegment,
  unitPx: number,
  theme: Theme,
  timeS: number,
): void {
  if (e.taken && (e.kind === 'orb' || e.kind === 'gear' || e.kind === 'boost')) return;
  const x = rec.x1 + entityX(e, timeS) * rec.w1;
  const base = rec.y1;
  const halfW = e.hw * rec.w1;
  if (halfW < 0.6) return;

  switch (e.kind) {
    case 'rock':
      drawRock(ctx, x, base, halfW, H_ROCK * unitPx, theme, e.rnd);
      drawHint(ctx, x, base - H_ROCK * unitPx - halfW * 0.7, 'dodge', halfW, timeS);
      break;
    case 'log':
      drawLog(ctx, x, base, halfW, H_LOG * unitPx, theme, e.rnd);
      drawHint(ctx, x, base - H_LOG * unitPx - halfW * 0.8, 'hop', halfW, timeS);
      break;
    case 'gate':
      drawGate(ctx, x, base, halfW, unitPx, theme, e.rnd, timeS);
      drawHint(ctx, x, base - 100 * unitPx - halfW * 0.5, 'duck', halfW, timeS);
      break;
    case 'pit':
      drawPit(ctx, rec, theme);
      // One marker for the whole gap, on its leading edge.
      if (!rec.seg.entities.some((o) => o.kind === 'pit' && o.seg < e.seg)) {
        drawHint(ctx, rec.x1, base - rec.w1 * 0.42, 'hop', rec.w1 * 0.34, timeS);
      }
      break;
    case 'traffic':
      drawTraffic(ctx, x, base, halfW, H_TRAFFIC * unitPx, theme, e.rnd, timeS);
      drawHint(ctx, x, base - H_TRAFFIC * unitPx - halfW * 0.6, 'dodge', halfW, timeS);
      break;
    case 'geyser':
      drawGeyser(ctx, x, base, halfW, unitPx, theme, e, timeS);
      drawHint(ctx, x, base - halfW * 1.6, 'dodge', halfW, timeS);
      break;
    case 'ramp':
      drawRamp(ctx, x, base, halfW, H_RAMP * unitPx, theme);
      break;
    case 'boost':
      drawBoostPad(ctx, x, base, halfW, theme, timeS);
      break;
    case 'orb':
      drawOrb(ctx, x, base - H_ORB * unitPx, halfW * 0.8, theme, timeS, e.rnd);
      break;
    case 'gear':
      drawGear(ctx, x, base - (e.clearY + 260) * unitPx, halfW, theme, timeS);
      break;
  }
}

// --- hazards ----------------------------------------------------------

function drawRock(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  hw: number,
  h: number,
  theme: Theme,
  rnd: number,
): void {
  const body = rockColor(theme);
  ctx.fillStyle = body;
  ctx.beginPath();
  const pts = 7;
  for (let i = 0; i <= pts; i++) {
    const a = Math.PI + (i / pts) * Math.PI;
    const wob = 0.72 + noise(rnd * 31 + i) * 0.45;
    ctx.lineTo(x + Math.cos(a) * hw * wob, base + Math.sin(a) * h * wob);
  }
  ctx.closePath();
  ctx.fill();
  // Lit face, so it reads as a solid object rather than a hole.
  ctx.fillStyle = shade(body, 0.22);
  ctx.beginPath();
  ctx.moveTo(x - hw * 0.15, base);
  ctx.lineTo(x - hw * 0.5, base - h * 0.55);
  ctx.lineTo(x + hw * 0.1, base - h * 0.72);
  ctx.lineTo(x + hw * 0.28, base);
  ctx.closePath();
  ctx.fill();
  // Hazard band at the base: pops against snow, tarmac, loam or void alike.
  ctx.fillStyle = DODGE_COLOUR;
  ctx.fillRect(x - hw * 0.92, base - h * 0.2, hw * 1.84, Math.max(1.2, h * 0.12));
  shadowBlob(ctx, x, base, hw * 1.05);
}

function rockColor(theme: Theme): string {
  switch (theme.id) {
    case 'antarctic':
      return '#7fb8dd';
    case 'volcano':
      return '#2b2429';
    case 'metro':
      return '#4a3a5c';
    case 'belt':
      return '#4a4266';
    case 'chroma':
      return '#6b3fa0';
    default:
      return '#4a3b2f';
  }
}

function drawLog(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  hw: number,
  h: number,
  theme: Theme,
  rnd: number,
): void {
  const c = theme.id === 'antarctic' ? '#9fd0ea' : theme.id === 'belt' ? '#3a3360' : '#5a3f2a';
  ctx.fillStyle = c;
  roundRect(ctx, x - hw, base - h, hw * 2, h, h * 0.42);
  ctx.fill();
  // Gold cap: the same colour as the hop marker, so the pairing is learned once.
  ctx.fillStyle = HOP_COLOUR;
  roundRect(ctx, x - hw, base - h, hw * 2, Math.max(1.2, h * 0.34), h * 0.2);
  ctx.fill();
  // End grain rings tell you instantly that this one is hoppable.
  ctx.fillStyle = shade(c, -0.3);
  ctx.beginPath();
  ctx.ellipse(x - hw, base - h * 0.5, hw * 0.14, h * 0.5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = shade(c, 0.35);
  ctx.lineWidth = Math.max(0.6, h * 0.07);
  ctx.beginPath();
  ctx.ellipse(x - hw, base - h * 0.5, hw * 0.07, h * 0.26, 0, 0, Math.PI * 2);
  ctx.stroke();
  shadowBlob(ctx, x, base, hw * 1.1);
  void rnd;
}

function drawGate(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  hw: number,
  unitPx: number,
  theme: Theme,
  rnd: number,
  timeS: number,
): void {
  const bottom = base - 100 * unitPx;
  const top = base - GATE_TOP * unitPx;
  const h = bottom - top;
  if (h < 1) return;
  // Posts.
  ctx.fillStyle = shade(theme.haze, -0.5);
  ctx.fillRect(x - hw, top, hw * 0.16, base - top);
  ctx.fillRect(x + hw * 0.84, top, hw * 0.16, base - top);
  // Banner.
  const g = ctx.createLinearGradient(x, top, x, bottom);
  g.addColorStop(0, theme.accent);
  g.addColorStop(1, theme.accent2);
  ctx.fillStyle = g;
  ctx.fillRect(x - hw, top, hw * 2, h);
  // Bunting scallops, so the underside edge is unmistakable.
  ctx.fillStyle = rgba('#000000', 0.35);
  const n = 5;
  for (let i = 0; i < n; i++) {
    const cx = x - hw + ((i + 0.5) / n) * hw * 2;
    ctx.beginPath();
    ctx.arc(cx, bottom, (hw * 2) / n / 2, 0, Math.PI);
    ctx.fill();
  }
  ctx.strokeStyle = rgba('#ffffff', 0.6 + 0.3 * Math.sin(timeS * 6 + rnd * 10));
  ctx.lineWidth = Math.max(1, h * 0.06);
  ctx.beginPath();
  ctx.moveTo(x - hw, bottom);
  ctx.lineTo(x + hw, bottom);
  ctx.stroke();
}

function drawPit(ctx: CanvasRenderingContext2D, rec: ProjectedSegment, theme: Theme): void {
  ctx.fillStyle = theme.id === 'belt' || theme.id === 'chroma' ? '#04030f' : '#0a0a10';
  ctx.beginPath();
  ctx.moveTo(rec.x1 - rec.w1, rec.y1);
  ctx.lineTo(rec.x1 + rec.w1, rec.y1);
  ctx.lineTo(rec.x2 + rec.w2, rec.y2);
  ctx.lineTo(rec.x2 - rec.w2, rec.y2);
  ctx.closePath();
  ctx.fill();
  // Warm lip on the near edge reads as "this is a gap, not a shadow".
  ctx.strokeStyle = HOP_COLOUR;
  ctx.lineWidth = Math.max(2, rec.w1 * 0.03);
  ctx.beginPath();
  ctx.moveTo(rec.x1 - rec.w1, rec.y1);
  ctx.lineTo(rec.x1 + rec.w1, rec.y1);
  ctx.stroke();
}

function drawTraffic(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  hw: number,
  h: number,
  theme: Theme,
  rnd: number,
  timeS: number,
): void {
  const body = theme.id === 'metro' ? (rnd > 0.5 ? '#ffd24d' : '#3ee0c8') : theme.accent2;
  ctx.fillStyle = shade(body, -0.55);
  roundRect(ctx, x - hw, base - h * 0.34, hw * 2, h * 0.34, h * 0.08);
  ctx.fill();
  ctx.fillStyle = body;
  roundRect(ctx, x - hw * 0.92, base - h, hw * 1.84, h * 0.72, h * 0.16);
  ctx.fill();
  ctx.fillStyle = rgba('#0a1020', 0.75);
  roundRect(ctx, x - hw * 0.66, base - h * 0.9, hw * 1.32, h * 0.3, h * 0.06);
  ctx.fill();
  // Tail lights pulse, which is the cue that this one moves.
  const blink = 0.55 + 0.45 * Math.sin(timeS * 7 + rnd * 12);
  ctx.fillStyle = rgba('#ff3b3b', blink);
  ctx.fillRect(x - hw * 0.86, base - h * 0.4, hw * 0.3, h * 0.1);
  ctx.fillRect(x + hw * 0.56, base - h * 0.4, hw * 0.3, h * 0.1);
  shadowBlob(ctx, x, base, hw * 1.15);
}

function drawGeyser(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  hw: number,
  unitPx: number,
  theme: Theme,
  e: Entity,
  timeS: number,
): void {
  const active = geyserActive(e, timeS);
  const phase = e.period > 0 ? (((timeS / e.period + e.phase) % 1) + 1) % 1 : 0;
  // Vent is always visible so the hazard is never a surprise.
  ctx.fillStyle = shade(theme.road.dark, -0.4);
  ctx.beginPath();
  ctx.ellipse(x, base, hw, hw * 0.28, 0, 0, Math.PI * 2);
  ctx.fill();

  if (!active) {
    // Tell-tale: glow ramps up over the second before it fires.
    const warn = phase > e.duty ? Math.max(0, (phase - 0.82) / 0.18) : 0;
    if (warn > 0) {
      ctx.fillStyle = rgba(theme.accent, warn * 0.7);
      ctx.beginPath();
      ctx.ellipse(x, base, hw * (0.6 + warn * 0.5), hw * 0.22, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    return;
  }

  const rise = Math.min(1, (phase / e.duty) * 2.6);
  const h = H_GEYSER * unitPx * rise;
  const g = ctx.createLinearGradient(x, base, x, base - h);
  const hot = theme.id === 'antarctic' ? '#cdefff' : theme.id === 'belt' ? '#7cf7ff' : '#ffd24d';
  const cool = theme.id === 'antarctic' ? '#63b6e0' : theme.id === 'belt' ? '#a05cff' : '#ff4d1a';
  g.addColorStop(0, rgba(hot, 0.95));
  g.addColorStop(0.5, rgba(cool, 0.75));
  g.addColorStop(1, rgba(cool, 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(x - hw * 0.85, base);
  ctx.quadraticCurveTo(x - hw * 0.4, base - h * 0.6, x - hw * 0.2, base - h);
  ctx.lineTo(x + hw * 0.2, base - h);
  ctx.quadraticCurveTo(x + hw * 0.4, base - h * 0.6, x + hw * 0.85, base);
  ctx.closePath();
  ctx.fill();
}

function drawRamp(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  hw: number,
  h: number,
  theme: Theme,
): void {
  const g = ctx.createLinearGradient(x, base - h, x, base);
  g.addColorStop(0, theme.accent);
  g.addColorStop(1, shade(theme.road.dark, -0.2));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(x - hw, base);
  ctx.lineTo(x + hw, base);
  ctx.lineTo(x + hw * 0.72, base - h);
  ctx.lineTo(x - hw * 0.72, base - h);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba('#ffffff', 0.8);
  ctx.beginPath();
  ctx.moveTo(x, base - h * 1.5);
  ctx.lineTo(x - hw * 0.34, base - h * 1.06);
  ctx.lineTo(x + hw * 0.34, base - h * 1.06);
  ctx.closePath();
  ctx.fill();
}

function drawBoostPad(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  hw: number,
  theme: Theme,
  timeS: number,
): void {
  for (let i = 0; i < 3; i++) {
    const p = ((timeS * 2.4 + i * 0.33) % 1);
    const y = base - hw * 0.5 * p;
    ctx.fillStyle = rgba(theme.accent2, (1 - p) * 0.9);
    ctx.beginPath();
    ctx.moveTo(x, y - hw * 0.34);
    ctx.lineTo(x - hw * 0.8, y + hw * 0.12);
    ctx.lineTo(x - hw * 0.4, y + hw * 0.12);
    ctx.lineTo(x, y - hw * 0.1);
    ctx.lineTo(x + hw * 0.4, y + hw * 0.12);
    ctx.lineTo(x + hw * 0.8, y + hw * 0.12);
    ctx.closePath();
    ctx.fill();
  }
}

// --- pickups ----------------------------------------------------------

function drawOrb(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  hw: number,
  theme: Theme,
  timeS: number,
  rnd: number,
): void {
  const bob = Math.sin(timeS * 3.4 + rnd * 9) * hw * 0.22;
  const r = hw * 0.55;
  const cy = y + bob;
  const g = ctx.createRadialGradient(x, cy, 0, x, cy, r * 1.9);
  g.addColorStop(0, rgba('#ffffff', 0.95));
  g.addColorStop(0.32, rgba(theme.accent, 0.9));
  g.addColorStop(1, rgba(theme.accent, 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, cy, r * 1.9, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(x, cy, r * 0.45, 0, Math.PI * 2);
  ctx.fill();
}

function drawGear(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  hw: number,
  theme: Theme,
  timeS: number,
): void {
  const r = hw * 0.9;
  const spin = timeS * 2.2;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r * 2.6);
  g.addColorStop(0, rgba('#fff3a8', 0.8));
  g.addColorStop(1, rgba('#ffd24d', 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r * 2.6, 0, Math.PI * 2);
  ctx.fill();

  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(spin);
  ctx.fillStyle = '#ffd24d';
  const teeth = 8;
  ctx.beginPath();
  for (let i = 0; i < teeth * 2; i++) {
    const a = (i / (teeth * 2)) * Math.PI * 2;
    const rr = i % 2 === 0 ? r : r * 0.72;
    ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = shade(theme.sky.top, 0.1);
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.34, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// --- roadside props ---------------------------------------------------

export function drawProp(
  ctx: CanvasRenderingContext2D,
  p: PropInstance,
  x: number,
  base: number,
  unitPx: number,
  theme: Theme,
  timeS: number,
): void {
  const h = p.scale * (unitPx * 1500);
  if (h < 1.5) return;
  const w = h * 0.34;
  switch (p.kind) {
    case 'redwood':
      trunk(ctx, x, base, w * 0.28, h * 0.82, p.color);
      // Rim light down one side so a stand of trunks does not read as a flat wall.
      ctx.fillStyle = shade(p.color, 0.3);
      ctx.fillRect(x + w * 0.12, base - h * 0.82, w * 0.07, h * 0.82);
      canopy(ctx, x, base - h * 0.62, w * 1.35, h * 0.44, canopyColor(theme, p.color), 4);
      break;
    case 'fern':
      fanLeaves(ctx, x, base, w * 1.4, h, p.color, 7);
      break;
    case 'stump':
      trunk(ctx, x, base, w * 0.7, h, p.color);
      ctx.fillStyle = shade(p.color, 0.3);
      ctx.beginPath();
      ctx.ellipse(x, base - h, w * 0.7, h * 0.16, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'spire':
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.moveTo(x - w * 0.6, base);
      ctx.lineTo(x + w * 0.15, base - h);
      ctx.lineTo(x + w * 0.7, base);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = rgba('#ffffff', 0.35);
      ctx.beginPath();
      ctx.moveTo(x + w * 0.15, base - h);
      ctx.lineTo(x + w * 0.7, base);
      ctx.lineTo(x + w * 0.3, base);
      ctx.closePath();
      ctx.fill();
      break;
    case 'penguin': {
      const wob = Math.sin(timeS * 2 + p.rnd * 10) * w * 0.08;
      ctx.fillStyle = p.color;
      roundRect(ctx, x - w * 0.4 + wob, base - h, w * 0.8, h, w * 0.4);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      roundRect(ctx, x - w * 0.24 + wob, base - h * 0.72, w * 0.48, h * 0.66, w * 0.24);
      ctx.fill();
      ctx.fillStyle = '#ffbe3c';
      ctx.beginPath();
      ctx.moveTo(x + wob, base - h * 0.78);
      ctx.lineTo(x + w * 0.26 + wob, base - h * 0.7);
      ctx.lineTo(x + wob, base - h * 0.64);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'igloo':
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(x, base, h, Math.PI, 0);
      ctx.fill();
      ctx.fillStyle = shade(p.color, -0.25);
      ctx.beginPath();
      ctx.arc(x, base, h * 0.36, Math.PI, 0);
      ctx.fill();
      break;
    case 'palm':
      ctx.strokeStyle = p.color;
      ctx.lineWidth = Math.max(1, w * 0.16);
      ctx.beginPath();
      ctx.moveTo(x, base);
      ctx.quadraticCurveTo(x + w * 0.3, base - h * 0.55, x + w * 0.15, base - h);
      ctx.stroke();
      fanLeaves(ctx, x + w * 0.15, base - h, w * 2.2, h * 0.4, shade(p.color, 0.25), 6);
      break;
    case 'totem':
      trunk(ctx, x, base, w * 0.55, h, p.color);
      for (let i = 0; i < 3; i++) {
        ctx.fillStyle = i % 2 === 0 ? '#ff7a3c' : '#f5e0c0';
        ctx.fillRect(x - w * 0.55, base - h * ((i + 1) / 3.4), w * 1.1, h * 0.09);
      }
      break;
    case 'lavaFountain': {
      const s = 0.4 + 0.6 * Math.abs(Math.sin(timeS * 1.4 + p.rnd * 8));
      const g = ctx.createLinearGradient(x, base, x, base - h * s);
      g.addColorStop(0, rgba('#ffd24d', 0.95));
      g.addColorStop(1, rgba('#ff4d1a', 0));
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.moveTo(x - w * 0.5, base);
      ctx.lineTo(x, base - h * s);
      ctx.lineTo(x + w * 0.5, base);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'neonSign': {
      const pulse = 0.6 + 0.4 * Math.sin(timeS * 3 + p.rnd * 14);
      ctx.strokeStyle = p.color;
      ctx.lineWidth = Math.max(1, w * 0.12);
      ctx.globalAlpha *= pulse;
      roundRect(ctx, x - w * 0.7, base - h, w * 1.4, h * 0.62, w * 0.12);
      ctx.stroke();
      ctx.globalAlpha /= pulse;
      ctx.fillStyle = shade(theme.haze, -0.5);
      ctx.fillRect(x - w * 0.06, base - h * 0.42, w * 0.12, h * 0.42);
      break;
    }
    case 'stall':
      ctx.fillStyle = shade(p.color, -0.5);
      ctx.fillRect(x - w * 0.8, base - h * 0.6, w * 1.6, h * 0.6);
      ctx.fillStyle = p.color;
      for (let i = 0; i < 4; i++) {
        ctx.fillStyle = i % 2 === 0 ? p.color : '#ffffff';
        ctx.fillRect(x - w * 0.9 + i * w * 0.45, base - h, w * 0.45, h * 0.4);
      }
      break;
    case 'lamp':
      ctx.fillStyle = p.color;
      ctx.fillRect(x - w * 0.05, base - h, w * 0.1, h);
      ctx.fillStyle = rgba('#ffe9a8', 0.9);
      ctx.beginPath();
      ctx.arc(x, base - h, w * 0.28, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'asteroid':
      drawRock(ctx, x, base - h * 0.4, w, h, theme, p.rnd);
      break;
    case 'satellite':
      ctx.fillStyle = p.color;
      ctx.fillRect(x - w * 0.2, base - h, w * 0.4, h * 0.4);
      ctx.fillStyle = shade(p.color, -0.3);
      ctx.fillRect(x - w * 1.1, base - h * 0.9, w * 0.8, h * 0.2);
      ctx.fillRect(x + w * 0.3, base - h * 0.9, w * 0.8, h * 0.2);
      break;
    case 'crystal': {
      const glow = 0.5 + 0.5 * Math.sin(timeS * 2 + p.rnd * 11);
      ctx.fillStyle = rgba(p.color, 0.55 + glow * 0.4);
      ctx.beginPath();
      ctx.moveTo(x, base - h);
      ctx.lineTo(x + w * 0.5, base - h * 0.35);
      ctx.lineTo(x + w * 0.25, base);
      ctx.lineTo(x - w * 0.25, base);
      ctx.lineTo(x - w * 0.5, base - h * 0.35);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'balloon': {
      const bob = Math.sin(timeS * 1.2 + p.rnd * 7) * h * 0.06;
      ctx.strokeStyle = rgba('#ffffff', 0.35);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, base);
      ctx.lineTo(x, base - h * 0.62 + bob);
      ctx.stroke();
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.ellipse(x, base - h * 0.78 + bob, w * 0.42, h * 0.22, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
  }
}

// --- shared bits ------------------------------------------------------

function trunk(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  w: number,
  h: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x - w, base);
  ctx.lineTo(x - w * 0.55, base - h);
  ctx.lineTo(x + w * 0.55, base - h);
  ctx.lineTo(x + w, base);
  ctx.closePath();
  ctx.fill();
}

function canopy(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  w: number,
  h: number,
  color: string,
  layers: number,
): void {
  ctx.fillStyle = color;
  for (let i = 0; i < layers; i++) {
    const t = i / layers;
    const yy = base + h * t * 0.7;
    const ww = w * (1 - t * 0.55);
    ctx.beginPath();
    ctx.moveTo(x, yy - h * 0.55);
    ctx.lineTo(x - ww, yy + h * 0.2);
    ctx.lineTo(x + ww, yy + h * 0.2);
    ctx.closePath();
    ctx.fill();
  }
}

function fanLeaves(
  ctx: CanvasRenderingContext2D,
  x: number,
  base: number,
  w: number,
  h: number,
  color: string,
  count: number,
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, h * 0.09);
  ctx.lineCap = 'round';
  for (let i = 0; i < count; i++) {
    const a = -Math.PI * 0.86 + (i / (count - 1)) * Math.PI * 0.72;
    ctx.beginPath();
    ctx.moveTo(x, base);
    ctx.quadraticCurveTo(
      x + Math.cos(a) * w * 0.35,
      base + Math.sin(a) * h * 0.7,
      x + Math.cos(a) * w * 0.5,
      base + Math.sin(a) * h * 0.55 + h * 0.12,
    );
    ctx.stroke();
  }
  ctx.lineCap = 'butt';
}

function shadowBlob(ctx: CanvasRenderingContext2D, x: number, base: number, r: number): void {
  ctx.fillStyle = rgba('#000000', 0.22);
  ctx.beginPath();
  ctx.ellipse(x, base, r, r * 0.2, 0, 0, Math.PI * 2);
  ctx.fill();
}

/** Canopies take their hue from the world's ground, not from the trunk. */
function canopyColor(theme: Theme, trunk: string): string {
  return theme.id === 'redwood' ? '#2f5738' : shade(trunk, -0.15);
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.max(0, Math.min(r, Math.min(Math.abs(w), Math.abs(h)) / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

export { hsl };
